import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, LogOut, Menu, PanelLeftClose, PanelLeftOpen, Search, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { useActiveRoute } from "@/navigation/useActiveRoute";
import { ADMIN_ENV, envBadgeLabel, type AdminEnv } from "@/lib/env";
import { ROLE_LABEL } from "@/lib/roles";
import { cn } from "@/lib/utils";

type HeaderProps = {
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  onOpenMobileNav: () => void;
  onOpenSearch: () => void;
  isMobile: boolean;
};

const ENV_STYLE: Record<AdminEnv, string> = {
  LOCAL: "bg-[hsl(var(--leaf-50))] text-[hsl(var(--leaf))] border-[hsl(var(--leaf))]/30",
  DEV: "bg-accent text-accent-foreground border-primary/30",
  STAGING: "bg-[hsl(var(--marigold-50))] text-[hsl(var(--marigold-ink))] border-[hsl(var(--marigold))]/50",
  PRODUCTION: "bg-[hsl(var(--rose))] text-white border-transparent",
  UNKNOWN: "bg-[hsl(var(--rose))] text-white border-transparent",
};

const ENV_HINT: Record<AdminEnv, string> = {
  LOCAL: "Local Supabase on this computer. Safe to experiment.",
  DEV: "Shared dev project. Testers may be using it.",
  STAGING: "Staging. Changes here are what testers sign off.",
  PRODUCTION: "Production. Every change reaches real readers.",
  UNKNOWN: "Unknown backend. Treat it like production.",
};

/** Top bar: breadcrumb, environment badge, user menu. Stays mounted across pages. */
const Header = ({ sidebarCollapsed, onToggleSidebar, onOpenMobileNav, onOpenSearch, isMobile }: HeaderProps) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { route, navRoute, group } = useActiveRoute();

  const crumbs: Array<{ label: string; to?: string }> = [];
  if (group && group.id !== "today") crumbs.push({ label: group.label });
  if (navRoute) crumbs.push({ label: navRoute.title, to: route?.path !== navRoute.path ? navRoute.path : undefined });
  if (route && navRoute && route.path !== navRoute.path) crumbs.push({ label: route.title });
  if (!navRoute && route) crumbs.push({ label: route.title });

  const handleSignOut = async () => {
    await signOut();
    navigate("/admin/login", { replace: true });
  };

  return (
    <header className="h-16 shrink-0 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 flex items-center gap-3 px-4 lg:px-6">
      {isMobile ? (
        <Button variant="ghost" size="icon" onClick={onOpenMobileNav} aria-label="Open navigation">
          <Menu className="h-5 w-5" />
        </Button>
      ) : (
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleSidebar}
          aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-pressed={sidebarCollapsed}
          className="text-ink-3"
        >
          {sidebarCollapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
        </Button>
      )}

      <nav aria-label="Breadcrumb" className="min-w-0 flex-1">
        <ol className="flex items-center gap-1.5 text-sm min-w-0">
          {crumbs.map((c, i) => {
            const last = i === crumbs.length - 1;
            return (
              <li key={`${c.label}-${i}`} className="flex items-center gap-1.5 min-w-0">
                {i > 0 && <ChevronRight className="h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" />}
                {c.to && !last ? (
                  <Link to={c.to} className="truncate text-ink-2 hover:text-foreground hover:underline underline-offset-4">
                    {c.label}
                  </Link>
                ) : (
                  <span
                    className={cn("truncate", last ? "font-semibold text-foreground" : "text-ink-2")}
                    aria-current={last ? "page" : undefined}
                  >
                    {c.label}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>

      <div className="flex items-center gap-2 shrink-0">
        {isMobile && (
          <Button variant="ghost" size="icon" onClick={onOpenSearch} aria-label="Search or jump to">
            <Search className="h-5 w-5" />
          </Button>
        )}
        <span
          className={cn(
            "h-7 px-2.5 rounded-full border text-xs font-bold tracking-[0.6px] inline-flex items-center",
            ENV_STYLE[ADMIN_ENV]
          )}
          title={ENV_HINT[ADMIN_ENV]}
          aria-label={`Environment: ${envBadgeLabel(ADMIN_ENV)}. ${ENV_HINT[ADMIN_ENV]}`}
        >
          {envBadgeLabel(ADMIN_ENV)}
        </span>

        {user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-10 gap-2 px-2 rounded-xl" aria-label="Account menu">
                <Avatar className="h-8 w-8">
                  {user.avatarUrl ? <AvatarImage src={user.avatarUrl} alt="" /> : null}
                  <AvatarFallback className="bg-primary text-primary-foreground font-bold">
                    {(user.name || user.email || "?").charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden md:inline max-w-[160px] truncate text-sm font-medium">{user.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="font-normal">
                <div className="text-sm font-semibold truncate">{user.name}</div>
                <div className="text-xs text-ink-3 truncate">{user.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5 text-xs text-ink-3">
                {user.roleIds
                  .filter((id) => id >= 1 && id <= 4)
                  .map((id) => ROLE_LABEL[id])
                  .join(" · ")}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => navigate("/admin/profile")}>
                <UserRound className="mr-2 h-4 w-4" aria-hidden="true" />
                Your profile
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={handleSignOut}>
                <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
};

export default Header;
